import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, X } from 'lucide-react';
import CommitNumberInput from './CommitNumberInput';
import { SavedNote, SwitchRow } from './instantSettings';
import { SheetField, SheetSection } from './sheet';

/**
 * Οι εκπτώσεις του ταμείου (take away και delivery — ίδια πεδία, άλλο endpoint): έτοιμα ποσοστά,
 * ελεύθερο ποσοστό / ποσό και τα μέγιστα όρια, σε ΜΙΑ ενότητα του «Φύλλου».
 * Τα ποσοστά: ετικέτες με «×» και πεδίο προσθήκης στη θέση του, με το λάθος δίπλα — πριν ένα
 * window.prompt του browser που απέρριπτε σιωπηλά.
 */
interface Props {
  presets: number[];
  onChange: (next: number[]) => void;
}

const DiscountPresetsField: React.FC<Props> = ({ presets, onChange }) => {
  const { t } = useTranslation(['settings']);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = presets || [];

  const add = () => {
    const value = Number(draft.replace(',', '.'));
    if (draft.trim() === '' || !Number.isFinite(value) || value <= 0 || value > 100) {
      setError(t('settings:discount.presetInvalid'));
      return;
    }
    if (list.some((p) => Number(p) === value)) {
      setError(t('settings:discount.presetExists', { value }));
      return;
    }
    onChange([...list, value].sort((a, b) => a - b));
    setDraft('');
    setError(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2">
        {list.map((preset, idx) => (
          <span key={`${preset}-${idx}`} className="inline-flex items-center gap-0.5 h-9 pl-3 pr-1 rounded-full border border-line-strong bg-surface-2 text-sm font-semibold text-t1 tabular-nums">
            {preset}%
            <button
              type="button"
              onClick={() => onChange(list.filter((_, i) => i !== idx))}
              aria-label={t('settings:discount.removePreset', { value: preset })}
              className="w-7 h-7 rounded-full flex items-center justify-center text-t3 hover:text-red-600 dark:hover:text-red-400 hover:bg-surface-3 transition-colors"
            >
              <X className="w-3.5 h-3.5" aria-hidden="true" />
            </button>
          </span>
        ))}
        <form
          className="flex items-center gap-1.5"
          onSubmit={(e) => { e.preventDefault(); add(); }}
        >
          <input
            type="text"
            inputMode="decimal"
            value={draft}
            onChange={(e) => { setDraft(e.target.value); setError(null); }}
            placeholder={t('settings:discount.presetPlaceholder')}
            aria-label={t('settings:discount.newPresetLabel')}
            aria-invalid={error ? true : undefined}
            className="w-24 h-9 px-3 rounded-full border border-line-strong bg-surface-2 text-t1 placeholder-t4 text-sm focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring"
          />
          <button
            type="submit"
            className="inline-flex items-center gap-1 h-9 px-3 rounded-full border border-line-strong text-sm font-medium text-t2 hover:bg-surface-3 hover:text-t1 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" aria-hidden="true" />
            {t('settings:discount.add')}
          </button>
        </form>
      </div>
      {error && <p role="alert" className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

export interface DiscountSettings {
  discount_presets: number[];
  discount_allow_custom_percent: boolean;
  discount_allow_fixed_amount: boolean;
  discount_max_percent: number | string;
  discount_max_amount: number | string;
}

interface DiscountSettingsSectionProps {
  /** 'takeaway' | 'delivery': ids της ενότητας και των πεδίων στο DOM. */
  idPrefix: string;
  settings: DiscountSettings;
  onChange: <K extends keyof DiscountSettings>(key: K, value: DiscountSettings[K]) => void;
  /** «Αποθηκεύτηκε» στην ενότητα. */
  saved: boolean;
}

export const discountSectionId = (idPrefix: string): string => `${idPrefix}-discounts`;

/** Η ενότητα «Εκπτώσεις»: κάθε αλλαγή φεύγει αμέσως με `onChange` (τα όρια όταν φύγει η εστίαση). */
export const DiscountSettingsSection: React.FC<DiscountSettingsSectionProps> = ({ idPrefix, settings, onChange, saved }) => {
  const { t } = useTranslation(['settings']);
  return (
    <SheetSection
      id={discountSectionId(idPrefix)}
      title={t('settings:discount.title')}
      indexLabel={t('settings:discount.indexLabel')}
      hint={t('settings:discount.subtitle')}
      aside={<SavedNote show={saved} />}
    >
      <SheetField label={t('settings:discount.presetsLabel')} hint={t('settings:discount.presetsHint')}>
        <DiscountPresetsField presets={settings.discount_presets || []} onChange={(next) => onChange('discount_presets', next)} />
      </SheetField>

      <div className="mt-5 pt-5 border-t border-line">
        <SwitchRow
          title={t('settings:discount.customPercent')}
          subtitle={t('settings:discount.customPercentDesc')}
          checked={settings.discount_allow_custom_percent}
          onChange={(next) => onChange('discount_allow_custom_percent', next)}
        />
        <SwitchRow
          title={t('settings:discount.fixedAmount')}
          subtitle={t('settings:discount.fixedAmountDesc')}
          checked={settings.discount_allow_fixed_amount}
          onChange={(next) => onChange('discount_allow_fixed_amount', next)}
        />
      </div>

      {/* Αποθήκευση όταν φύγει η εστίαση (CommitNumberInput), όχι σε κάθε πλήκτρο. */}
      <div className="mt-5 pt-5 border-t border-line">
        <div className="xs-grid2">
          <SheetField label={t('settings:discount.maxPercent')} htmlFor={`${idPrefix}-max-percent`}>
            <CommitNumberInput
              id={`${idPrefix}-max-percent`}
              min={1}
              max={100}
              value={settings.discount_max_percent}
              onCommit={(val) => onChange('discount_max_percent', val)}
            />
          </SheetField>
          <SheetField label={t('settings:discount.maxAmount')} htmlFor={`${idPrefix}-max-amount`}>
            <CommitNumberInput
              id={`${idPrefix}-max-amount`}
              min={1}
              value={settings.discount_max_amount}
              onCommit={(val) => onChange('discount_max_amount', val)}
            />
          </SheetField>
        </div>
        <p className="xs-note">{t('settings:discount.safetyLimits')}</p>
      </div>
    </SheetSection>
  );
};

export default DiscountPresetsField;
